import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  CalendarClock,
  Droplets,
  Wine,
  AlertTriangle,
  PenTool,
  Layers,
  Weight,
  Boxes,
  Sparkles,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile : 3 engagements ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison à domicile
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de cartons à domicile garantie à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que la ponctualité et la sécurité de vos fournitures sont
            le point de départ de vos préparatifs, nous avons développé un
            réseau logistique de proximité sans aucune faille. Découvrez
            nos engagements fermes pour votre adresse à {city.name}.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: "Acheminement 100% interne",
              body: "Nous excluons systématiquement les sous-traitants ou les plateformes de messagerie générales. Nos propres équipes chargent, transportent et déposent vos colis, vous assurant un soin chirurgical à chaque manipulation.",
              Icon: Truck,
            },
            {
              title: "Garantie d'intégrité à la dépose",
              body: "Nos camions capitonnés protègent vos produits contre l'humidité ambiante et la poussière des axes routiers. Vos cartons arrivent parfaitement secs, rigides et immédiatement prêts pour le montage.",
              Icon: ShieldCheck,
            },
            {
              title: "Respect strict de vos plannings",
              body: "Nous convenons ensemble d'une plage horaire précise pour la livraison à domicile ou en entreprise, vous évitant de bloquer inutilement votre journée.",
              Icon: CalendarClock,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Technique pro : emballer les liquides ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Left visual + danger callout */}
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFE9D6] border border-[#FF7A00]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
              <Droplets className="h-3.5 w-3.5" strokeWidth={2.6} />
              Technique pro
            </div>
            <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Comment emballer les liquides sans risquer l&apos;inondation
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Les bouteilles de vin, les huiles de cuisine ou les produits
              d&apos;entretien ménager représentent un risque majeur&nbsp;:
              une seule fuite peut détruire l&apos;intégralité des cartons
              situés en dessous dans le camion. Voici le protocole pour
              étanchéifier vos liquides.
            </p>

            <div className="mt-7 relative overflow-hidden bg-[#fff5e8] border border-[#FF7A00]/30 rounded-2xl p-5 md:p-6">
              <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-[#FF7A00]/20 blur-2xl" />
              <div className="relative flex items-start gap-3">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#FF7A00] text-white shrink-0">
                  <AlertTriangle className="h-5 w-5" strokeWidth={2.5} />
                </span>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#FF7A00]">
                    Risque n°1
                  </div>
                  <h3 className="mt-1 font-display font-extrabold text-[15px] text-[#0f1d2b]">
                    Une seule fuite détruit tout le chargement
                  </h3>
                  <p className="mt-2 text-[13.5px] text-[#3a4856] leading-relaxed">
                    Dans le camion, un carton inondé contamine tous ceux
                    qui l&apos;entourent — vêtements, papiers, livres,
                    électronique. La prévention coûte 10 fois moins cher
                    que la perte.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to="/produit/film-etirable"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
                data-testid="city-vaucresson-cta-film"
              >
                <Layers className="h-4 w-4" strokeWidth={2.6} />
                Voir le film étirable
                <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
              </Link>
              <Link
                to="/produit/papier-bulle-10m"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
                data-testid="city-vaucresson-cta-bulle"
              >
                Voir le papier bulle
              </Link>
            </div>
          </div>

          {/* Right: 3 protocol steps */}
          <div className="lg:col-span-7 space-y-3">
            {[
              {
                title: "Le scellage au film étirable",
                body: "Dévissez le bouchon de vos flacons entamés, appliquez un morceau de film plastique étirable directement sur le goulot, puis revissez fermement le bouchon par-dessus. Cela crée un joint hermétique imbattable.",
                Icon: Wine,
              },
              {
                title: "Le double bouclier étanche",
                body: "Tapissez l'intérieur de votre carton avec un grand sac poubelle ultra-résistant. Placez ensuite vos bouteilles bien droites à l'intérieur, idéalement séparées par des croisillons ou enveloppées individuellement dans du film à bulles.",
                Icon: ShieldCheck,
              },
              {
                title: "Le signalement visuel",
                body: "Refermez le sac poubelle, scellez le carton et inscrivez au marqueur rouge épais sur les flancs du module la mention « HAUT / BAS » accompagnée de flèches, pour imposer un transport exclusivement vertical aux porteurs.",
                Icon: PenTool,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-3 bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
              >
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white shrink-0">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Timeline verticale : étapes fondamentales ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#cfdbe6] text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5] mb-4">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.6} />
            Méthodologie chronologique
          </div>
          <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Les étapes fondamentales pour réussir tous vos colis
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour éviter la casse et optimiser le volume utile dans le
            véhicule de déménagement, appliquez cette feuille de route
            chronologique lors du montage de chaque boîte.
          </p>
        </div>

        <div className="mt-12 relative">
          {/* Vertical connecting line (desktop only) */}
          <div
            aria-hidden="true"
            className="hidden md:block absolute left-9 top-2 bottom-2 w-0.5 bg-gradient-to-b from-[#237cb5] via-[#237cb5] to-[#FF7A00]"
          />

          <ol className="space-y-6 md:space-y-7">
            {[
              {
                n: "01",
                title: "Le verrouillage du fond (la règle du \"H\")",
                body: "Ne vous contentez pas de croiser les rabats. Appliquez une longue bande d'adhésif sur la jointure centrale, puis deux autres bandes sur les côtés latéraux. Cette technique consolide la structure et empêche le fond de céder sous la charge.",
                Icon: Boxes,
                cta: {
                  to: "/produit/rouleau-adhesif",
                  label: "Voir le rouleau d'adhésif",
                  testId: "city-vaucresson-cta-adhesif",
                },
              },
              {
                n: "02",
                title: "La répartition intelligente des masses",
                body: "Placez toujours les objets les plus denses et lourds au fond (livres, dossiers, fonds de vaisselle) et complétez les espaces supérieurs avec des éléments légers (linge de maison, oreillers, peluches). Le poids total ne doit jamais dépasser 20 kg.",
                Icon: Weight,
                cta: {
                  to: "/produit/carton-a-livres",
                  label: "Voir le carton à livres",
                  testId: "city-vaucresson-cta-livres",
                },
              },
              {
                n: "03",
                title: "La chasse aux espaces vides",
                body: "Un carton à moitié vide va s'écraser sous le poids des autres piles dans le camion. Utilisez du papier de calage, du film bulle ou des vêtements pour combler les interstices. Le contenu doit être totalement immobile : si vous secouez doucement le carton, rien ne doit bouger.",
                Icon: Layers,
                cta: {
                  to: "/emballage-protection",
                  label: "Voir les consommables de calage",
                  testId: "city-vaucresson-cta-calage",
                },
              },
            ].map(({ n, title, body, Icon, cta }) => (
              <li key={n} className="relative md:pl-24">
                {/* Big number circle */}
                <div className="md:absolute md:left-0 md:top-0 flex md:block items-center gap-3 mb-3 md:mb-0">
                  <span className="flex items-center justify-center h-[72px] w-[72px] rounded-full bg-white border-2 border-[#237cb5] text-[#237cb5] font-display font-extrabold text-2xl shadow-[0_8px_20px_-12px_rgba(35,124,181,0.45)]">
                    {n}
                  </span>
                </div>

                <div className="bg-white rounded-2xl border border-[#cfdbe6] p-6 md:p-7">
                  <div className="flex items-center gap-2.5">
                    <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#FF7A00] text-white">
                      <Icon className="h-5 w-5" strokeWidth={2.4} />
                    </span>
                    <h3 className="font-display font-extrabold text-[16px] md:text-[17px] text-[#0f1d2b] leading-tight">
                      {title}
                    </h3>
                  </div>
                  <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                  <div className="mt-4">
                    <Link
                      to={cta.to}
                      className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#237cb5] hover:gap-2.5 transition-all"
                      data-testid={cta.testId}
                    >
                      {cta.label}
                      <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
                    </Link>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-12">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-vaucresson-cta-shop"
          >
            Voir tous les cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-vaucresson-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
